import { School, Scores } from '@/store'
import SchoolService from '@/services/SchoolService'
import ApiService from '@/services/ApiService'
import GameService from '@/services/GameService'
import SubService from '@/services/SubService'

const DAY_LENGTH = 24 * 60 * 60 * 1000;// in milliseconds
export const STREAK_LENGTH = 3;// number of consecutive days to count as a streak
export const STREAK_REWARD = 1000;// number of points to award for a streak
export const JOURNEY_DAY_REWARD = 100;// number of points to award for a journey day

export const CONTENT_TYPES = [
  'school_topic_pages',
  'quiz_questions',
  'game_questions'
]

export default {
  /**
   * Offline implementation of the streak days
   * @param {UserJourneyDay[]} userJourneyDays Array of user journey days
   * @param {Date} now Date for which to calculate the streak
   * @returns {[]} Array of streak days
   */
  getStreakDaysArray(userJourneyDays, now = new Date()) {
    // Sort the days by ordinal in descending order
    userJourneyDays.sort((a, b) => b.journey_day_ordinal - a.journey_day_ordinal)

    // Get the start of today. Clone input date to avoid mutation.
    const startOfToday = new Date(now)
    startOfToday.setHours(0, 0, 0, 0)

    // Get first possible start of the streak
    const maxStartOfStreak = new Date(startOfToday - (DAY_LENGTH * STREAK_LENGTH))

    let startOfStreak = new Date(startOfToday)
    // collect all days that are part of the streak
    // they are part of the streak if they are completed_at within the last 3 days and are consecutive
    const streak = userJourneyDays
      .filter(d => Boolean(d.completed_at))
      .reduce((streak, item) => {
        const completed = new Date(item.completed_at)

        const diff = this.countDaysBetweenDates(completed, startOfStreak)
        if (diff < 2 && completed > maxStartOfStreak) {
          startOfStreak = new Date(item.completed_at)
          streak.push(item)
        }

        return streak
      }, [])

    streak.reverse()

    return streak
  },

  /**
   * Get the user journey days in the current streak. Server side implementation.
   *
   * @param {UserJourneyDay[]} userJourneyDays
   * @param {Number} user_id
   */
  async getStreakDays(userJourneyDays, user_id) {
    const { data: streak } = await ApiService.getUserStreak({ user_id })

    // TODO: Validate that streak current day is not older than yesterday

    if (streak) {
      const idx = userJourneyDays.findIndex(d => d.id === streak.started_by_id)
      return idx > -1 ? userJourneyDays.slice(idx) : []
    } else {
      return []
    }
  },

  /**
   *
   * @param {UserStreak} streak
   * @param {UserJourneyDay[]} userJourneyDays
   * @returns {Promise<{Number}>}
   */
   getStreakStartOrdinal(streak, userJourneyDays) {
     if (!streak) return null

     const day = userJourneyDays.find(d => d.id === streak.started_by_id)?.journey_day_ordinal
     return day?.journey_day_ordinal
  },

  countDaysBetweenDates (from, to) {
    const oneDay = 24 * 60 * 60 * 1000 // hours * minutes * seconds * milliseconds
    const firstDate = (new Date(from)).setHours(0, 0, 0, 0)
    const secondDate = (new Date(to)).setHours(0, 0, 0, 0)

    return Math.round(Math.abs((firstDate - secondDate) / oneDay))
  },

  /**
   * Find out the ordinal for the current day.
   *
   * @param userJournalDays
   * @param maxOrdinal
   * @returns {number}
   */
  getCurrentOrdinal(userJournalDays, maxOrdinal = null) {
    // Get the start of today
    const startOfToday = new Date()
    startOfToday.setHours(0, 0, 0, 0)

    let currentOrdinal = 1

    const lastCompletedUserDay = userJournalDays
      .slice()
      .sort((a, b) => b.journey_day_ordinal - a.journey_day_ordinal)
      .find(day => day.completed_at)

    // Ordinal of the next day that will be available to the user. Last completed + 1 but < max available
    if (lastCompletedUserDay) {
      currentOrdinal = lastCompletedUserDay.journey_day_ordinal

      // console.log('-> getCurrentOrdinal', lastCompletedUserDay.completed_at, startOfToday, this.countDaysBetweenDates(lastCompletedUserDay.completed_at, startOfToday))
      if (this.countDaysBetweenDates(lastCompletedUserDay.completed_at, startOfToday) >= 1) {
        currentOrdinal = maxOrdinal ? Math.min(currentOrdinal + 1, maxOrdinal) : currentOrdinal + 1
      }
    }

    return currentOrdinal
  },

  /**
   * Get the journey days from the ordinal. It will construct the plan dynamically and set pro/free info.
   *
   * @param {Number} from
   * @param {Number} limit
   * @returns {Promise<Awaited<{data: {id: *, ordinal: *, package_id: number, school_topic_pages: boolean, quiz_questions: boolean, game_questions: boolean}[]}>>}
   */
  async getJourneyDays(from, limit = 10) {
    // const max = // get total number of school_topic_pages
    const topics = await SchoolService.getTopics()
    const pages = topics
      .flatMap(t => t.school_topic_pages)
      .filter(p => !School.getTopicPages().includes(p.original_id))

    // figure out if "school_topic_pages" is available
    const attributes = ['school_topic_pages', 'quiz_questions', 'game_questions']

    const data = Array.from({ length: limit }, (_, i) => {
      const ordinal = Number(from) + i
      const available = ['quiz_questions']

      // add game_questions if the game is not locked
      if (!GameService.isLocked(Scores.getBalance(), SubService.state.plan)) {
        available.push('game_questions')
      }

      // if pages are available, add school_topic_pages to the available list
      if (pages?.length > 0) {
        available.unshift('school_topic_pages')
      }

      const res = {
        id: ordinal,
        ordinal,
        package_id: 1,// update later when setting up the content
      }

      // add all content attributes as false
      attributes.forEach((type) => {
        res[type] = false
      })

      // set the available ones to true
      // school_topic_pages: (ordinal - 1) % 3 === 0,// 1, 4, 7, etc.
      // quiz_questions: (ordinal - 2) % 3 === 0,// 2, 5, 8, etc.
      // game_questions: (ordinal - 3) % 3 === 0// 3, 6, 9, etc.
      available.forEach((type, index) => {
        res[type] = (ordinal - (index + 1)) % available.length === 0
      })

      // populate school_topic_pages with original_id
      if (res.school_topic_pages) {

        // get and remove the first page
        const page = pages.shift()

        res.school_topic_pages = [page.id]
        res.package_id = page.package_id
      }

      return res
    })

    return Promise.resolve({ data })
  },

  async getJourneyDay(ordinal) {
    const { data } = await this.getJourneyDays(ordinal, 1)
    return data[0] ?? null
  },


  /**
   * Build array of available content for the journey day
   * School topic days will get the id of the page
   *
   * @param {Object} journeyDay - The journey day object containing attributes: 'school_topic_pages', 'quiz_questions', 'game_questions'
   * @returns {{type: string, id?: number}[]}
   */
  buildContentArray(journeyDay) {
    return CONTENT_TYPES
      // Filter out the content types that are not available
      .filter((type) => journeyDay[type])
      // Push the content type and id into the content array
      .reduce((agr, type) => {
        if (Array.isArray(journeyDay[type])) {
          journeyDay[type]
            .forEach((id) => agr.push({ type, id }))
        } else {
          agr.push({ type })
        }

        return agr
      }, [])
  }
}
